/** Данные окна «Отчеты»: список отчётов, диапазоны периода, фильтры и подсказки. */

import { t } from "@/lib/i18n";

export type ReportTone = "ok" | "bad" | "neutral";

export type ReportType = {
  id: string;
  label: string;
  icon: string;
  title: string;
  description: string;
  overview: string;
  tableTitle: string;
  /** стабильное число для «шума» в графике */
  seed: number;
  /** ровно две серии: успешная и неуспешная */
  series: { label: string; tone: ReportTone }[];
  metrics: [string, string];
  rows: { name: string; values: [number, number] }[];
};

export const reportMeta = {
  timeZone: t("Европа/Рим (GMT+01:00)"),
  info: t("Цифры считаются по событиям за выбранный период времени. Если фильтры не находят ни одного события, график остаётся пустым."),
  emptyFiltered: t("Выбранный(ы) фильтр(ы) не содержит(ют) подходящих данных."),
  emptyPlain: t("Данных за выбранный период нет — включите демо-данные в шапке окна."),
};

export const reportRanges = [
  { id: "7", label: t("Последние 7 дней") },
  { id: "14", label: t("Последние 14 дней") },
  { id: "30", label: t("Последние 30 дней") },
  { id: "month", label: t("Этот месяц") },
];

export const reportFilterGroups = [
  { id: "channel", label: t("Канал"), options: ["WhatsApp", "Telegram", "Email", t("Виджет")] },
  { id: "status", label: t("Статус"), options: [t("Завершено"), t("Ошибка"), t("В процессе")] },
  { id: "team", label: t("Команда"), options: [t("Продажи"), t("Поддержка"), t("Администраторы")] },
];

export const reportHints = [
  {
    icon: "ri-calendar-line",
    title: t("Период считается по доставке"),
    text: t("Событие попадает в отчёт по времени доставки, а не создания. Поэтому смена периода может сдвинуть цифры на границе дней."),
  },
  {
    icon: "ri-filter-3-line",
    title: t("Фильтры сужают выборку"),
    text: t("Каждый добавленный фильтр отсекает часть событий. Три фильтра сразу могут дать пустой результат — это нормально, снимите один чип."),
  },
  {
    icon: "ri-download-2-line",
    title: t("Отчёт можно выгрузить"),
    text: t("В демо-режиме выгрузка недоступна, но в рабочем пространстве любой отчёт сохраняется в CSV вместе с применёнными фильтрами."),
  },
];

export const reportTypes: ReportType[] = [
  {
    id: "lifecycle",
    label: t("Жизненный цикл"),
    icon: "ri-refresh-line",
    title: t("Жизненный цикл"),
    description:
      t("Отчёт показывает, как контакты двигаются по этапам воронки: сколько появилось, дошло до сделки и потерялось за выбранный период. Считается по истории смены этапов."),
    overview: t("Обзор этапов"),
    tableTitle: t("Результаты по этапам"),
    seed: 3,
    series: [
      { label: t("Продвинулись"), tone: "ok" },
      { label: t("Потеряны"), tone: "bad" },
    ],
    metrics: [t("Контактов продвинулось"), t("Контактов потеряно")],
    rows: [
      { name: t("Новые лиды"), values: [142, 18] },
      { name: t("В работе"), values: [96, 22] },
      { name: t("Квалифицированы"), values: [68, 14] },
      { name: t("Сделка"), values: [41, 9] },
      { name: t("Выиграно"), values: [27, 6] },
    ],
  },
  {
    id: "calls",
    label: t("Звонки"),
    icon: "ri-phone-line",
    title: t("Звонки"),
    description:
      t("Отчёт по входящим, исходящим и внутренним звонкам: длительность, принятые и пропущенные вызовы. Помогает увидеть часы, когда команда не успевает отвечать."),
    overview: t("Обзор звонков"),
    tableTitle: t("Результаты по типам звонков"),
    seed: 5,
    series: [
      { label: t("Принятые"), tone: "ok" },
      { label: t("Пропущенные"), tone: "bad" },
    ],
    metrics: [t("Звонки приняты"), t("Звонки пропущены")],
    rows: [
      { name: t("Входящие"), values: [318, 24] },
      { name: t("Исходящие"), values: [186, 31] },
      { name: t("Внутренние"), values: [64, 7] },
      { name: t("Обратные звонки"), values: [38, 5] },
    ],
  },
  {
    id: "conversations",
    label: t("Беседы"),
    icon: "ri-chat-3-line",
    title: t("Беседы"),
    description:
      t("Отчёт по диалогам: сколько открыто, закрыто и осталось без ответа. Разбивка по каналам показывает, откуда приходит больше всего обращений."),
    overview: t("Обзор бесед"),
    tableTitle: t("Результаты по каналам"),
    seed: 7,
    series: [
      { label: t("Закрытые"), tone: "ok" },
      { label: t("Открытые"), tone: "neutral" },
    ],
    metrics: [t("Беседы закрыты"), t("Беседы открыты")],
    rows: [
      { name: "WhatsApp", values: [420, 38] },
      { name: "Telegram", values: [268, 21] },
      { name: "Email", values: [132, 19] },
      { name: t("Виджет на сайте"), values: [88, 12] },
    ],
  },
  {
    id: "replies",
    label: t("Ответы"),
    icon: "ri-question-answer-line",
    title: t("Ответы"),
    description:
      t("Отчёт о скорости первого ответа: сколько диалогов закрыто в SLA, сколько просрочено и сколько осталось без ответа вовсе."),
    overview: t("Обзор ответов"),
    tableTitle: t("Результаты по времени ответа"),
    seed: 11,
    series: [
      { label: t("В срок"), tone: "ok" },
      { label: t("Просрочено"), tone: "bad" },
    ],
    metrics: [t("Ответы в SLA"), t("Ответы просрочены")],
    rows: [
      { name: t("До 5 минут"), values: [284, 0] },
      { name: t("До 30 минут"), values: [126, 0] },
      { name: t("До 2 часов"), values: [64, 12] },
      { name: t("Без ответа"), values: [0, 38] },
    ],
  },
  {
    id: "resolutions",
    label: t("Резолюции"),
    icon: "ri-checkbox-circle-line",
    title: t("Резолюции"),
    description:
      t("Отчёт по закрытию диалогов: кто закрыл беседу — AI-агент, продавец или поддержка — и сколько диалогов пришлось эскалировать людям."),
    overview: t("Обзор резолюций"),
    tableTitle: t("Результаты по исполнителям"),
    seed: 13,
    series: [
      { label: t("Решено"), tone: "ok" },
      { label: t("Эскалировано"), tone: "bad" },
    ],
    metrics: [t("Диалогов решено"), t("Эскалировано людям")],
    rows: [
      { name: t("AI-агенты"), values: [212, 18] },
      { name: t("Продавцы"), values: [168, 9] },
      { name: t("Поддержка"), values: [94, 14] },
      { name: t("Руководители"), values: [26, 3] },
    ],
  },
  {
    id: "messages",
    label: t("Сообщения"),
    icon: "ri-message-3-line",
    title: t("Сообщения"),
    description:
      t("Отчёт по объёму переписки: сколько сообщений ушло клиентам и сколько пришло. Разбивка по каналам помогает планировать нагрузку на смену."),
    overview: t("Обзор сообщений"),
    tableTitle: t("Результаты по каналам"),
    seed: 17,
    series: [
      { label: t("Исходящие"), tone: "neutral" },
      { label: t("Входящие"), tone: "ok" },
    ],
    metrics: [t("Сообщений отправлено"), t("Сообщений получено")],
    rows: [
      { name: "WhatsApp", values: [1840, 1520] },
      { name: "Telegram", values: [1120, 940] },
      { name: "Email", values: [410, 380] },
      { name: t("Виджет на сайте"), values: [286, 240] },
    ],
  },
  {
    id: "contacts",
    label: t("Контакты"),
    icon: "ri-contacts-book-2-line",
    title: t("Контакты"),
    description:
      t("Отчёт по базе клиентов: откуда появились новые контакты, сколько записей обновили вручную и сколько удалили или объединили как дубли."),
    overview: t("Обзор контактов"),
    tableTitle: t("Результаты по источникам"),
    seed: 19,
    series: [
      { label: t("Созданы"), tone: "ok" },
      { label: t("Удалены"), tone: "bad" },
    ],
    metrics: [t("Контактов создано"), t("Контактов удалено")],
    rows: [
      { name: t("Из диалогов"), values: [148, 12] },
      { name: t("Импорт CSV"), values: [620, 34] },
      { name: t("Вручную"), values: [42, 3] },
      { name: t("Интеграции"), values: [88, 6] },
    ],
  },
  {
    id: "tasks",
    label: t("Задания"),
    icon: "ri-task-line",
    title: t("Задания"),
    description:
      t("Отчёт по задачам команды: сколько заданий закрыто в срок, сколько просрочено и какие типы заданий чаще всего зависают."),
    overview: t("Обзор заданий"),
    tableTitle: t("Результаты по типам заданий"),
    seed: 23,
    series: [
      { label: t("Выполнены"), tone: "ok" },
      { label: t("Просрочены"), tone: "bad" },
    ],
    metrics: [t("Задания выполнены"), t("Задания просрочены")],
    rows: [
      { name: t("Звонки"), values: [86, 12] },
      { name: t("Письма"), values: [124, 8] },
      { name: t("Встречи"), values: [38, 5] },
      { name: t("Проверки"), values: [52, 9] },
    ],
  },
  {
    id: "leaderboard",
    label: t("Таблица лидеров"),
    icon: "ri-trophy-line",
    title: t("Таблица лидеров"),
    description:
      t("Рейтинг сотрудников по закрытым диалогам и соблюдению SLA. Цифры считаются по событиям выбранного периода, а не по общему стажу."),
    overview: t("Обзор рейтинга"),
    tableTitle: t("Результаты по сотрудникам"),
    seed: 29,
    series: [
      { label: t("В срок"), tone: "ok" },
      { label: t("Просрочено"), tone: "bad" },
    ],
    metrics: [t("Диалогов закрыто в срок"), t("Диалогов просрочено")],
    rows: [
      { name: t("Анна Ковалёва"), values: [84, 4] },
      { name: t("Лев Орлов"), values: [72, 9] },
      { name: t("Мира Соколова"), values: [68, 6] },
      { name: t("Игорь Дёмин"), values: [54, 11] },
    ],
  },
  {
    id: "users",
    label: t("Пользователи"),
    icon: "ri-user-line",
    title: t("Пользователи"),
    description:
      t("Отчёт по участникам рабочего пространства: кто был активен, кто не заходил в систему и кому ограничили доступ."),
    overview: t("Обзор пользователей"),
    tableTitle: t("Результаты по ролям"),
    seed: 31,
    series: [
      { label: t("Активные"), tone: "ok" },
      { label: t("Неактивные"), tone: "bad" },
    ],
    metrics: [t("Активных пользователей"), t("Без активности")],
    rows: [
      { name: t("Администраторы"), values: [3, 0] },
      { name: t("Менеджеры"), values: [6, 1] },
      { name: t("Агенты"), values: [14, 2] },
      { name: t("Наблюдатели"), values: [4, 1] },
    ],
  },
  {
    id: "broadcasts",
    label: t("Трансляции"),
    icon: "ri-mail-send-line",
    title: t("Трансляции"),
    description:
      t("Этот отчёт поможет получить представление о результатах трансляции. В отчете учитываются только трансляции, отправленные за выбранный период времени на основе времени доставки. Узнайте больше"),
    overview: t("Обзор трансляций"),
    tableTitle: t("Результаты по каналам"),
    seed: 37,
    series: [
      { label: t("Завершенный"), tone: "ok" },
      { label: t("Неуспешный"), tone: "bad" },
    ],
    metrics: [t("Трансляции завершены"), t("Трансляции провалены")],
    rows: [
      { name: "WhatsApp", values: [42, 3] },
      { name: "Telegram", values: [28, 2] },
      { name: "Email", values: [36, 5] },
      { name: t("Виджет на сайте"), values: [12, 1] },
    ],
  },
];